import { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import { Logo } from "@/components/Logo";
import { NAVSERVICES } from "@/data/navservices";

const ALL_SERVICES = NAVSERVICES || [];
const SEO_SERVICES = ALL_SERVICES.filter((s) => s.group === "seo");
const WEBDEV_SERVICES = ALL_SERVICES.filter((s) => s.group === "webdev");
const SMO_SERVICES = ALL_SERVICES.filter((s) => s.group === "smo");

const LINKS = [
  { to: "/", label: "Home" },
  { to: "/portfolio", label: "Portfolio" },
  { to: "/blog", label: "Blog" },
  { to: "/pricing", label: "Pricing" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

function MenuColumn({ title, items, accent = "text-rw-green" }) {
  return (
    <div>
      <p className={`font-mono text-[10px] uppercase tracking-[0.22em] ${accent} mb-3`}>{title}</p>
      <ul className="space-y-1.5">
        {items.map((s) => (
          <li key={s.slug}>
            <Link to={`/services/${s.slug}`} data-testid={`menu-service-${s.slug}`}
              className="block text-[13px] text-rw-muted hover:text-rw-green transition-colors leading-snug">
              {s.name}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function Navbar() {
  const [open, setOpen] = useState(false);
  const [svcOpen, setSvcOpen] = useState(false);

  return (
    <header className="fixed top-0 inset-x-0 z-50 border-b border-rw-border bg-rw-bg/80 backdrop-blur-xl" data-testid="navbar">
      <div className="container-rw flex h-20 items-center justify-between">
        <Link to="/" aria-label="Ravi Works home" data-testid="nav-logo-link" onClick={() => setOpen(false)}>
          <Logo />
        </Link>

        <nav className="hidden lg:flex items-center gap-1" aria-label="Primary" data-testid="nav-desktop">
          <NavLink to="/" data-testid="nav-link-home"
            className={({ isActive }) => `px-3.5 py-2 text-sm font-medium rounded-lg transition-colors duration-200 ${isActive ? "text-rw-green" : "text-rw-muted hover:text-rw-text"}`}>
            Home
          </NavLink>

          <div className="relative group" data-testid="nav-services-dropdown">
            <NavLink to="/services" data-testid="nav-link-services"
              className={({ isActive }) => `inline-flex items-center gap-1 px-3.5 py-2 text-sm font-medium rounded-lg transition-colors duration-200 ${isActive ? "text-rw-green" : "text-rw-muted hover:text-rw-text"}`}>
              Services <ChevronDown size={14} className="transition-transform duration-200 group-hover:rotate-180" />
            </NavLink>
            <div className="invisible absolute left-1/2 top-full z-50 w-[880px] max-w-[90vw] -translate-x-1/2 pt-3 opacity-0 transition-all duration-200 group-hover:visible group-hover:opacity-100 group-focus-within:visible group-focus-within:opacity-100">
              <div className="rounded-2xl border border-rw-border bg-rw-bg2/95 backdrop-blur-xl p-7 shadow-2xl shadow-black/40">
                <div className="grid grid-cols-4 gap-7">
                  <MenuColumn title="SEO Services" items={SEO_SERVICES.slice(0, 8)} />
                  <MenuColumn title="More SEO" items={SEO_SERVICES.slice(8, 16)} />
                  <MenuColumn title="Specialized SEO" items={SEO_SERVICES.slice(16, 24)} />
                  <div className="space-y-7">
                    <MenuColumn title="Website Development" items={WEBDEV_SERVICES} accent="text-rw-blue" />
                    <MenuColumn title="SMO" items={SMO_SERVICES.slice(0, 3)} accent="text-rw-dim" />
                  </div>
                </div>
                <div className="mt-6 flex items-center justify-between border-t border-rw-border pt-5">
                  <Link to="/services" data-testid="menu-all-services"
                    className="inline-flex items-center gap-2 text-sm font-semibold text-rw-green hover:gap-3 transition-all">
                    All Services →
                  </Link>
                  <Link to="/contact" data-testid="menu-services-cta"
                    className="rounded-full bg-rw-green px-5 py-2 text-xs font-semibold text-rw-bg hover:bg-rw-green2 transition-colors">
                    Get SEO Audit
                  </Link>
                </div>
              </div>
            </div>
          </div>

          {LINKS.slice(1).map((l) => (
            <NavLink key={l.to} to={l.to} data-testid={`nav-link-${l.label.toLowerCase()}`}
              className={({ isActive }) => `px-3.5 py-2 text-sm font-medium rounded-lg transition-colors duration-200 ${isActive ? "text-rw-green" : "text-rw-muted hover:text-rw-text"}`}>
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:block">
          <Link to="/contact" data-testid="nav-cta-audit"
            className="inline-flex items-center rounded-full bg-rw-green px-5 py-2.5 text-sm font-semibold text-rw-bg transition-all duration-200 hover:bg-rw-green2 hover:-translate-y-0.5">
            Get SEO Audit
          </Link>
        </div>

        <button className="lg:hidden p-2 text-rw-text" onClick={() => setOpen(!open)}
          aria-label={open ? "Close menu" : "Open menu"} aria-expanded={open} data-testid="nav-mobile-toggle">
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      {open && (
        <nav className="lg:hidden border-t border-rw-border bg-rw-bg px-4 pb-6 pt-2 max-h-[80vh] overflow-y-auto" aria-label="Mobile" data-testid="nav-mobile">
          <NavLink to="/" onClick={() => setOpen(false)} data-testid="nav-mobile-link-home"
            className={({ isActive }) => `block px-3 py-3.5 text-base font-medium border-b border-rw-border/60 ${isActive ? "text-rw-green" : "text-rw-muted"}`}>
            Home
          </NavLink>

          <button onClick={() => setSvcOpen(!svcOpen)} aria-expanded={svcOpen} data-testid="nav-mobile-services-toggle"
            className="flex w-full items-center justify-between px-3 py-3.5 text-base font-medium text-rw-muted border-b border-rw-border/60">
            Services <ChevronDown size={18} className={`transition-transform duration-200 ${svcOpen ? "rotate-180" : ""}`} />
          </button>
          {svcOpen && (
            <div className="border-b border-rw-border/60 py-2" data-testid="nav-mobile-services-list">
              <Link to="/services" onClick={() => setOpen(false)} data-testid="nav-mobile-all-services"
                className="block px-5 py-2.5 text-sm font-semibold text-rw-green">
                All Services →
              </Link>
              <p className="px-5 pt-3 pb-1 font-mono text-[10px] uppercase tracking-[0.2em] text-rw-green">SEO Services</p>
              {SEO_SERVICES.map((s) => (
                <Link key={s.slug} to={`/services/${s.slug}`} onClick={() => setOpen(false)}
                  data-testid={`nav-mobile-service-${s.slug}`}
                  className="block px-5 py-2 text-sm text-rw-muted hover:text-rw-green transition-colors">
                  {s.name}
                </Link>
              ))}
              <p className="px-5 pt-3 pb-1 font-mono text-[10px] uppercase tracking-[0.2em] text-rw-blue">Website Development</p>
              {WEBDEV_SERVICES.map((s) => (
                <Link key={s.slug} to={`/services/${s.slug}`} onClick={() => setOpen(false)}
                  data-testid={`nav-mobile-service-${s.slug}`}
                  className="block px-5 py-2 text-sm text-rw-muted hover:text-rw-blue transition-colors">
                  {s.name}
                </Link>
              ))}
              <p className="px-5 pt-3 pb-1 font-mono text-[10px] uppercase tracking-[0.2em] text-rw-dim">SMO</p>
              {SMO_SERVICES.map((s) => (
                <Link key={s.slug} to={`/services/${s.slug}`} onClick={() => setOpen(false)}
                  data-testid={`nav-mobile-service-${s.slug}`}
                  className="block px-5 py-2 text-sm text-rw-muted hover:text-rw-blue transition-colors">
                  {s.name}
                </Link>
              ))}
            </div>
          )}

          {LINKS.slice(1).map((l) => (
            <NavLink key={l.to} to={l.to} onClick={() => setOpen(false)} data-testid={`nav-mobile-link-${l.label.toLowerCase()}`}
              className={({ isActive }) => `block px-3 py-3.5 text-base font-medium border-b border-rw-border/60 ${isActive ? "text-rw-green" : "text-rw-muted"}`}>
              {l.label}
            </NavLink>
          ))}
          <NavLink to="/faq" onClick={() => setOpen(false)} data-testid="nav-mobile-link-faq"
            className={({ isActive }) => `block px-3 py-3.5 text-base font-medium border-b border-rw-border/60 ${isActive ? "text-rw-green" : "text-rw-muted"}`}>
            FAQ
          </NavLink>
          <Link to="/contact" onClick={() => setOpen(false)} data-testid="nav-mobile-cta"
            className="mt-4 flex items-center justify-center rounded-full bg-rw-green px-5 py-3.5 text-sm font-semibold text-rw-bg">
            Get SEO Audit
          </Link>
        </nav>
      )}
    </header>
  );
}
